// ClientRepoPanel — per-client private git repo (2026-08-31).
// Rendered on the Client & Website Registry client page. Backed by
// /api/client-repos/* — plugin lists, web-asset inventories, notes, all
// version-controlled in a private git repo on the MIDTC server.
import { useState, useEffect, useCallback } from "react";
import { GitBranch, FileText, Plus, History, Trash2 } from "lucide-react";

const API = "https://ticketing.midtc.com/api";
const tok = () => localStorage.getItem("midtc_token") || "";
const apiFetch = (path, opts = {}) =>
  fetch(API + path, { headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" }, ...opts });

const fmtDT = (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
const fmtSize = (b) => b > 1024 ? `${(b / 1024).toFixed(1)} KB` : `${b} B`;

export default function ClientRepoPanel({ client }) {
  const [repo, setRepo]     = useState(null);   // {exists, files, commits}
  const [loading, setLoading] = useState(true);
  const [file, setFile]     = useState(null);   // {path, content}
  const [dirty, setDirty]   = useState(false);
  const [commitMsg, setCommitMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState("");
  const [showLog, setShowLog] = useState(false);
  const [newPath, setNewPath] = useState("");
  const [addingFile, setAddingFile] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/client-repos/${client.id}`);
      setRepo(await r.json());
    } catch { setErr("Could not load repo"); }
    finally { setLoading(false); }
  }, [client.id]);
  useEffect(() => { load(); }, [load]);

  const init = async () => {
    setErr("");
    const r = await apiFetch(`/client-repos/${client.id}/init`, { method: "POST" });
    if (!r.ok) { const d = await r.json(); setErr(d.error || "Init failed"); return; }
    load();
  };

  const openFile = async (path) => {
    setErr("");
    const r = await apiFetch(`/client-repos/${client.id}/file?path=${encodeURIComponent(path)}`);
    const d = await r.json();
    if (!r.ok) { setErr(d.error || "Could not open file"); return; }
    setFile(d); setDirty(false); setCommitMsg("");
  };

  const saveFile = async () => {
    if (!file) return;
    setSaving(true); setErr("");
    try {
      const r = await apiFetch(`/client-repos/${client.id}/file`, {
        method: "PUT",
        body: JSON.stringify({ path: file.path, content: file.content, message: commitMsg || `Update ${file.path}` }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || "Save failed"); return; }
      setDirty(false); setCommitMsg("");
      load();
    } finally { setSaving(false); }
  };

  const deleteFile = async (path) => {
    if (!window.confirm(`Delete ${path} from the repo? (History keeps the old versions.)`)) return;
    const r = await apiFetch(`/client-repos/${client.id}/file`, { method: "DELETE", body: JSON.stringify({ path }) });
    if (!r.ok) { const d = await r.json(); setErr(d.error || "Delete failed"); return; }
    if (file?.path === path) setFile(null);
    load();
  };

  const createFile = () => {
    const p = newPath.trim().replace(/^\/+/, "");
    if (!p) return;
    setFile({ path: p, content: "" });
    setDirty(true); setCommitMsg(`Add ${p}`);
    setAddingFile(false); setNewPath("");
  };

  const panel = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 14, overflow: "hidden", marginBottom: 18, boxShadow: "var(--t-shadow-panel, none)" };
  const ghost = { display: "inline-flex", alignItems: "center", gap: 6, background: "var(--t-input)", border: "1px solid var(--t-border-soft)", color: "var(--t-text-muted)", borderRadius: 9, padding: "7px 13px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" };

  return (
    <div style={panel}>
      {/* Head */}
      <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "13px 18px", borderBottom: "1px solid var(--t-border-soft)" }}>
        <GitBranch size={16} style={{ color: "#10b981" }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: "var(--t-text-primary)", flex: 1 }}>
          Private Repository {repo?.exists && <span style={{ fontWeight: 500, color: "var(--t-text-faint)", fontSize: 12 }}>· {repo.files?.length || 0} file{repo.files?.length !== 1 ? "s" : ""} · {repo.commits?.length ? `last commit ${fmtDT(repo.commits[0].date)}` : "no commits"}</span>}
        </span>
        {repo?.exists && (
          <>
            <button style={ghost} onClick={() => setShowLog(s => !s)}><History size={13} /> {showLog ? "Hide history" : "History"}</button>
            <button style={ghost} onClick={() => setAddingFile(a => !a)}><Plus size={13} /> New file</button>
          </>
        )}
      </div>

      <div style={{ padding: 16 }}>
        {err && <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#f87171", borderRadius: 8, padding: "8px 12px", fontSize: 12.5, marginBottom: 12 }}>{err}</div>}

        {loading && <div style={{ color: "var(--t-text-faint)", fontSize: 13 }}>Loading repo…</div>}

        {!loading && repo && !repo.exists && (
          <div style={{ textAlign: "center", padding: "18px 0" }}>
            <div style={{ fontSize: 13, color: "var(--t-text-muted)", marginBottom: 12, lineHeight: 1.6 }}>
              No repo yet for <b>{client.name}</b>. Initializing creates a private, version-controlled home for this
              client's <b>plugin list</b>, <b>web assets</b> and working notes (seeded with templates).
            </div>
            <button onClick={init}
              style={{ background: "linear-gradient(135deg,#10b981,#0891b2)", color: "#fff", border: "none", borderRadius: 9, padding: "9px 22px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              <GitBranch size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Initialize Private Repo
            </button>
          </div>
        )}

        {!loading && repo?.exists && (
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
            {/* File list */}
            <div style={{ width: 250, flexShrink: 0 }}>
              {addingFile && (
                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  <input autoFocus value={newPath} onChange={e => setNewPath(e.target.value)} onKeyDown={e => e.key === "Enter" && createFile()}
                    placeholder="e.g. deploy-notes.md"
                    style={{ flex: 1, background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "6px 9px", color: "var(--t-text-primary)", fontSize: 12 }} />
                  <button onClick={createFile} style={{ ...ghost, padding: "6px 10px" }}>Add</button>
                </div>
              )}
              {(repo.files || []).map(f => (
                <div key={f.path}
                  style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 9px", borderRadius: 8, cursor: "pointer", background: file?.path === f.path ? "var(--t-surface-mid)" : "transparent", border: `1px solid ${file?.path === f.path ? "rgba(37,99,235,0.4)" : "transparent"}` }}
                  onClick={() => openFile(f.path)}>
                  <FileText size={13} style={{ color: "#60a5fa", flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: "var(--t-text-body)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.path}</span>
                  <span style={{ fontSize: 10, color: "var(--t-text-faint)", flexShrink: 0 }}>{fmtSize(f.size)}</span>
                  <button onClick={e => { e.stopPropagation(); deleteFile(f.path); }} title="Delete file"
                    style={{ background: "none", border: "none", color: "var(--t-text-faint)", cursor: "pointer", padding: 0, display: "flex" }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              {!repo.files?.length && <div style={{ fontSize: 12, color: "var(--t-text-faint)" }}>Repo is empty.</div>}

              {showLog && (
                <div style={{ marginTop: 12, borderTop: "1px solid var(--t-border-soft)", paddingTop: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--t-text-faint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Recent commits</div>
                  {(repo.commits || []).map(c => (
                    <div key={c.hash} style={{ padding: "4px 0", borderBottom: "1px solid var(--t-border-soft)" }}>
                      <div style={{ fontSize: 11.5, color: "var(--t-text-body)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.subject}>{c.subject}</div>
                      <div style={{ fontSize: 10, color: "var(--t-text-faint)" }}>{c.author} · {fmtDT(c.date)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Editor */}
            <div style={{ flex: 1, minWidth: 280 }}>
              {!file ? (
                <div style={{ textAlign: "center", padding: 40, color: "var(--t-text-faint)", fontSize: 13, border: "1px dashed var(--t-border-soft)", borderRadius: 10 }}>
                  Select a file to view/edit — every save is a git commit.
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <span style={{ fontFamily: "monospace", fontSize: 12.5, color: "var(--t-text-primary)", fontWeight: 700, flex: 1 }}>{file.path}{dirty ? " •" : ""}</span>
                    <button onClick={() => { setFile(null); setDirty(false); }} style={{ background: "none", border: "none", color: "var(--t-text-faint)", fontSize: 17, cursor: "pointer", lineHeight: 1 }}>×</button>
                  </div>
                  <textarea value={file.content} onChange={e => { setFile(f => ({ ...f, content: e.target.value })); setDirty(true); }}
                    rows={14} spellCheck={false}
                    style={{ width: "100%", boxSizing: "border-box", background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 9, padding: 12, color: "var(--t-text-primary)", fontSize: 12.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", lineHeight: 1.6, resize: "vertical" }} />
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <input value={commitMsg} onChange={e => setCommitMsg(e.target.value)}
                      placeholder={`Commit message (default: "Update ${file.path}")`}
                      style={{ flex: 1, background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "7px 10px", color: "var(--t-text-body)", fontSize: 12 }} />
                    <button onClick={saveFile} disabled={saving || !dirty}
                      style={{ background: dirty ? "linear-gradient(135deg,#10b981,#0891b2)" : "var(--t-surface-mid)", color: dirty ? "#fff" : "var(--t-text-faint)", border: "none", borderRadius: 7, padding: "7px 18px", fontSize: 12.5, fontWeight: 700, cursor: dirty ? "pointer" : "default", fontFamily: "inherit" }}>
                      {saving ? "Committing…" : "Commit"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
